import { Badge, Icons, Stack, Switch, Table, Typography } from '@healthinal/ui';
import { createFileRoute } from '@tanstack/react-router';
import { differenceInMilliseconds } from 'date-fns';
import { HTMLProps, useEffect, useState } from 'react';
import { PatientAddressDto } from '../../../api/generated/model';
import { useGetPatientAddressesSuspense } from '../../../api/generated/patient-controller/patient-controller.ts';
import { TooltipCell } from '../../../components/table/TooltipCell.tsx';
import { TypographyLink } from '../../../components/TypographyLink.tsx';
import {
  getAddressReferenceTypeGroupName,
  getAddressReferenceTypeNameForNetworkTable,
  getAddressTypeName,
} from '../../../utils/address.ts';
import { groupBy } from '../../../utils/array.ts';
import { formatDateWithGermanMonth, isAfterToday } from '../../../utils/date.ts';

export const Route = createFileRoute('/patients/$patientId/network')({
  component: Network,
  staticData: { title: 'Netzwerk' },
});

function Network() {
  const { patientId } = Route.useParams();

  const { data: addresses } = useGetPatientAddressesSuspense(patientId);
  const groupedAddresses = groupBy(addresses, (address) => getAddressReferenceTypeGroupName(address.referenceType));

  const [showChronology, setShowChronology] = useState(false);
  return (
    <>
      <Stack direction="row" alignItems="start" gap={2}>
        <Typography level={'h3'}>Netzwerk</Typography>
        <Switch
          checked={showChronology}
          onChange={() => setShowChronology(!showChronology)}
          endDecorator={'Chronologie anzeigen'}
        />
      </Stack>
      <Table stickyHeader borderAxis="x" noWrap hoverRow>
        <thead>
          <tr>
            <th style={{ width: 30 }} />
            <th style={{ width: 110 }}>Von</th>
            <th style={{ width: 110 }}>Bis</th>
            <th>Rolle</th>
            <th>Name/Organisation</th>
            <th>Kontaktperson</th>
            <th>Erstellt von</th>
          </tr>
        </thead>
        <tbody>
          {Object.keys(groupedAddresses).map((addressGroup) => (
            <ExpendableTableRow
              isChronology={showChronology}
              key={addressGroup}
              addressGroup={addressGroup}
              patientAddresses={groupedAddresses[addressGroup]}
            />
          ))}
        </tbody>
      </Table>
    </>
  );
}

function ExpendableTableRow({
  addressGroup,
  patientAddresses,
  isChronology,
}: {
  addressGroup: string;
  patientAddresses: PatientAddressDto[];
  isChronology: boolean;
}) {
  const [isRowOpen, setIsRowOpen] = useState(!isChronology);

  useEffect(() => {
    setIsRowOpen(!isChronology);
  }, [isChronology]);

  return (
    <>
      <tr onClick={() => setIsRowOpen(!isRowOpen)}>
        <td style={{ paddingInline: 0 }}>{isRowOpen ? <Icons.ExpandMore /> : <Icons.ExpandLess />}</td>
        <td colSpan={6}>{addressGroup}</td>
      </tr>
      {isRowOpen &&
        (isChronology
          ? patientAddresses
          : patientAddresses.filter(
              (address) => !isChronology && (address.until === null || isAfterToday(address.until)),
            )
        )
          .sort((a, b) => {
            const fromComparison = differenceInMilliseconds(b.from, a.from);
            if (fromComparison !== 0) {
              return fromComparison;
            }
            if (a.until === null && b.until !== null) return -1;
            if (a.until !== null && b.until === null) return 1;
            return 0;
          })
          .map((item) => <NetworkRow key={item.id} patientAddress={item} />)}
    </>
  );
}

interface NetworkRowProps extends HTMLProps<HTMLTableRowElement> {
  patientAddress: PatientAddressDto;
}

function NetworkRow({ patientAddress, ...trProps }: NetworkRowProps) {
  let homepageWithHttps = patientAddress.address?.homepage;
  if (homepageWithHttps && !homepageWithHttps.startsWith('http')) {
    homepageWithHttps = 'https://' + homepageWithHttps;
  }
  return (
    <tr {...trProps}>
      <td>
        {(!patientAddress.until || isAfterToday(patientAddress.until)) && <Badge color={'success'} size={'sm'} />}
      </td>
      <td>{formatDateWithGermanMonth(patientAddress.from)}</td>
      <td>{formatDateWithGermanMonth(patientAddress.until)}</td>
      <TooltipCell>
        <Typography>{getAddressTypeName(patientAddress.address.addressType)}</Typography>
        {getAddressReferenceTypeNameForNetworkTable(patientAddress.referenceType) && (
          <Typography level={'body-sm'}>
            {getAddressReferenceTypeNameForNetworkTable(patientAddress.referenceType)}
          </Typography>
        )}
      </TooltipCell>
      <TooltipCell style={{ verticalAlign: 'top' }}>
        <Typography>{patientAddress.address.organization} </Typography>
        <Typography level={'body-sm'}>{patientAddress.address.address}</Typography>
        <Typography level={'body-sm'}>
          {patientAddress.address.zip} {patientAddress.address.city}
        </Typography>
        <TypographyLink href={`mailto:${patientAddress.address.email}`}>{patientAddress.address.email}</TypographyLink>
        <TypographyLink href={`tel:${patientAddress.address.phone}`}>{patientAddress.address.phone}</TypographyLink>
        <TypographyLink href={homepageWithHttps ?? ''} target={'_blank'}>
          {patientAddress.address.homepage}
        </TypographyLink>
      </TooltipCell>
      <TooltipCell style={{ verticalAlign: 'top' }}>
        <Typography>{patientAddress.contact?.name}</Typography>
        <Typography level={'body-sm'}>{patientAddress.contact?.function}</Typography>
        <TypographyLink href={`mailto:${patientAddress.contact?.email}`}>
          {patientAddress.contact?.email}
        </TypographyLink>
        <TypographyLink href={`tel:${patientAddress.contact?.phone}`}>{patientAddress.contact?.phone}</TypographyLink>
      </TooltipCell>
      <TooltipCell>
        {patientAddress.createdBy?.lastName} {patientAddress.createdBy?.firstName}
      </TooltipCell>
    </tr>
  );
}
